import { forkJoin as observableForkJoin, Observable, ReplaySubject, timer, BehaviorSubject } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter, map, take, tap, switchMap } from 'rxjs/operators';
import { HttpMonitorsService } from "../http/monitor/http-monitors.service";
import { RequestManagerService } from "./request-manager.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import * as moment from "moment";
import * as _ from "lodash";
import { EventSubject } from "./EventSubject";
import { AuthenticationService } from "../http/authentication/authentication.service";
import * as i0 from "@angular/core";
import * as i1 from "@angular/common/http";
import * as i2 from "./request-manager.service";
import * as i3 from "../http/authentication/authentication.service";
export class MonitorDataService extends HttpMonitorsService {
    constructor(httpClient, requestManager, authenticationService) {
        super(httpClient);
        this.httpClient = httpClient;
        this.requestManager = requestManager;
        this.authenticationService = authenticationService;
        // Caching subjects: These keep the fetched data.
        this.allMonitors = new ReplaySubject(1);
        this.allMonitorsForStatusOverview = new ReplaySubject(1);
        this.detailOverviewSubject = new EventSubject({});
        this.firmwareStatusSubject = new BehaviorSubject(null);
        this.chat = new BehaviorSubject({});
        this.compareMeasurements = (one, two) => one.lastMeasuredDateTime === two.lastMeasuredDateTime && one.measurements.length !== two.measurements.length;
        this.authenticationService.getAuthenticatedUser()
            .pipe(distinctUntilChanged((obj1, obj2) => obj1 && obj2 && obj1.id === obj2.id))
            .subscribe((user) => user ? this.initialize() : this.clear());
    }
    initialize() {
        this.fetchStatus();
        if (environment.prefetchMeasurements) {
            this.initDetailOverviews();
        }
        this.refresh = this.refresh || setInterval(() => this.fetchStatus(), environment.pollingIntervalInSeconds * 5000);
    }
    clear() {
        clearInterval(this.refresh);
        this.refresh = null;
        this.allMonitors.next(null);
        this.allMonitorsForStatusOverview.next(null);
        this.detailOverviewSubject.next({});
    }
    /**
     * Accessor methods. These are overloaded from HttpMonitorsService, and utilize the caching before request.
     */
    // This function is no longer supposed to be used. Use 'getAllForStatusOverview' instead.
    getAll() {
        return this.allMonitors
            .pipe(filter(monitors => !_.isNil(monitors)));
    }
    getAllForStatusOverview() {
        return this.allMonitorsForStatusOverview
            .pipe(filter(monitors => !_.isNil(monitors)));
    }
    getOneForStatusOverview(id) {
        return this.allMonitorsForStatusOverview
            .pipe(filter(monitors => !_.isNil(monitors)), distinctUntilChanged((one, two) => JSON.stringify(one) === JSON.stringify(two)), map((obj) => obj.filter(obj => obj.id == id)[0]), filter(obj => !_.isNil(obj)));
    }
    getForDetailOverview(id, selectedDateRange, detailStatusOverview) {
        // The default range is cached. We don't need to send a new request.
        this.startRefreshInterval(id, selectedDateRange);
        return this.detailOverviewSubject
            .pipe(map(obj => obj[id]), filter(obj => !_.isNil(obj)));
    }
    addMonitor(activationCode, serialNumber) {
        return super.addMonitor(activationCode, serialNumber)
            .pipe(tap(() => this.clear()), tap(() => this.fetchStatus()));
    }
    addByKey(serial, role, key, email) {
        return super.addByKey(serial, role, key, email)
            .pipe(tap(() => setTimeout(() => this.fetchStatus(), 1)));
    }
    deleteMonitor(id) {
        return super.deleteMonitor(id)
            .pipe(tap(() => this.fetchStatus()));
    }
    setChangedOrder(id, order) {
        return super.setChangedOrder(id, order);
    }
    /**
     * These functions refresh the cached data.
     */
    fetchStatus() {
        this.requestManager.addToQueue({
            priority: 1, observable: super.getAllForStatusOverview(), name: "MonitorStatus",
            subscriptionFunc: (monitors) => { this.allMonitorsForStatusOverview.next(monitors); }
        });
    }
    startRefreshInterval(id, selectedDateRange) {
        timer(0, environment.pollingIntervalInSeconds * 1000).pipe(
        // We automatically unsubscribe to the timer (and thus stop updating) when we unsubscribe the measurement update source.
        takeUntil(this.detailOverviewSubject.onUnsubscribe()))
            .subscribe(() => {
            this.updateMeasurements(id, selectedDateRange);
            this.updateChat(id);
            //this.initDetailOverview(id);
        });
    }
    updateChat(id) {
        this.chat
            .pipe(take(1))
            .subscribe(chat => {
            super.getChatForMonitor(id).subscribe(m => {
                chat[id] = m.concat().sort((a, b) => (new Date(a.creationtime)).getTime() - (new Date(b.creationtime)).getTime());
                this.chat.next(chat);
            });
        });
    }
    updateMeasurements(id, selectedDateRange) {
        if (selectedDateRange.end.isSame(moment(), "day")) {
            selectedDateRange.end = moment();
        }
        // Fetch the latest to update.
        observableForkJoin([
            this.getDetailOverviewForCache(),
            this.getOneForStatusOverview(id).pipe(take(1))
        ])
            .subscribe(results => {
            super.getForDetailOverview(id, selectedDateRange, results[1])
                .subscribe((updated) => {
                results[0][id] = updated;
                this.detailOverviewSubject.next(results[0]);
            });
        });
    }
    _skipCacheDetailOverview(id, selectedDateRange) {
        return super.getForDetailOverview(id, selectedDateRange);
    }
    /**
     * These functions initialize the needed data.
     */
    initDetailOverviews() {
        this.getAllForStatusOverview().pipe(take(1))
            .subscribe(monitors => monitors.forEach(monitor => this.initDetailOverview(monitor.id, monitor)));
    }
    initDetailOverview(monitorId, statusOverview) {
        const subscriptionFunc = detailOverview => this.getDetailOverviewForCache()
            .pipe(take(1))
            .subscribe(obj => {
            obj[monitorId] = detailOverview;
            this.detailOverviewSubject.next(obj);
        });
        // Request for the detail overview
        const observable = super.getForDetailOverview(monitorId, MonitorDataService.defaultTime(), statusOverview);
        this.requestManager.addToQueue({ priority: 10, observable, subscriptionFunc });
    }
    // This function is no longer supposed to be used. All data is now in Statusoverview.
    fetchAllMonitors() {
        this.requestManager.addToQueue({
            priority: 8,
            observable: super.getAll(),
            subscriptionFunc: (monitors) => this.allMonitors.next(monitors)
        });
    }
    static isDefault(selectedDateRange) {
        return selectedDateRange.start.isSame(this.defaultTime().start, "day") && selectedDateRange.end.isSame(this.defaultTime().end, "day");
    }
    getDetailOverviewForCache() {
        return this.detailOverviewSubject
            .pipe(take(1));
    }
    requestFirmwareUpgrade(id) {
        return super.requestFirmwareUpgrade(id)
            .pipe(tap((o) => this.firmwareStatusSubject.next(o)));
    }
    getFirmwareUpgradeStatus(id) {
        return this.firmwareStatusSubject.pipe(switchMap(() => super.getFirmwareUpgradeStatus(id)));
    }
    getChatForMonitor(id) {
        return this.chat.pipe(map(c => c[id]));
    }
    postChatForMonitor(id, text) {
        if (!text) {
            return Observable.empty();
        }
        return super.postChatForMonitor(id, text).pipe(tap(() => this.updateChat(id)));
    }
}
/**
 * Helper methods: these method are a shorthand for tasks.
 */
MonitorDataService.defaultTime = () => ({ start: moment().subtract(1, 'month'), end: moment() });
MonitorDataService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function MonitorDataService_Factory() { return new MonitorDataService(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.RequestManagerService), i0.ɵɵinject(i3.AuthenticationService)); }, token: MonitorDataService, providedIn: "root" });
